import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { inr } from "../lib/format";
import { BTN } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const KEYS = [["basic", "Basic", "earnings"], ["hra", "House Rent Allowance", "earnings"], ["special_allowance", "Special Allowance", "earnings"],
  ["pf", "Provident Fund", "deductions"], ["professional_tax", "Professional Tax", "deductions"], ["tds", "TDS", "deductions"]];

export const SalaryDialog = ({ employee, onClose }) => {
  const [data, setData] = useState(null);
  const [ov, setOv] = useState({});

  const load = () => api.get(`/employees/${employee.id}/salary-preview`).then((r) => {
    setData(r.data);
    setOv(Object.fromEntries(KEYS.map(([k]) => [k, r.data.override[k] ?? ""])));
  });
  useEffect(() => {
    if (employee) load();
    else setData(null);
  }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (values) => {
    const body = Object.fromEntries(KEYS.map(([k]) => [k, values[k] === "" || values[k] == null ? null : Number(values[k])]));
    try {
      await api.put(`/employees/${employee.id}/salary-structure`, body);
      toast.success("Salary structure saved");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const f = data?.formula;
  return (
    <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="salary-dialog">
        <DialogHeader><DialogTitle>Payroll breakup · {employee?.name}</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="text-sm text-[#6E6E73]">
              Annual CTC <b className="text-[#1D1D1F]">{inr(data.ctc_annual)}</b> · {data.entity} formula: Basic {f.basic_pct}% · HRA {f.hra_pct}% of Basic · PF {f.pf_pct}%{f.pf_wage_cap ? ` (cap ${inr(f.pf_wage_cap)})` : ""} · PT {inr(f.pt_amount)} · TDS {f.tds_auto ? "auto" : "off"}
            </div>
            <p className="text-xs text-[#6E6E73]">Leave a custom amount empty to use the company formula. Custom amounts are monthly, for a full month.</p>
            <div className="divide-y divide-[#F0F0F2]">
              {KEYS.map(([k, label, group]) => (
                <div key={k} className="grid grid-cols-3 gap-3 items-center py-2.5" data-testid={`salary-row-${k}`}>
                  <div className="text-sm font-semibold">{label} <span className="text-[10px] uppercase text-[#6E6E73] block font-medium">{group === "earnings" ? "Earning" : "Deduction"}</span></div>
                  <div className="text-sm tabular-nums">
                    {inr(data.breakup[group][k])}
                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${data.breakup.overridden.includes(k) ? "bg-[#FDF6ED] text-[#C87515]" : "bg-neutral-100 text-neutral-500"}`}>
                      {data.breakup.overridden.includes(k) ? "Custom" : "Formula"}
                    </span>
                  </div>
                  <Input type="number" min="0" placeholder="Custom amount" value={ov[k]} onChange={(e) => setOv({ ...ov, [k]: e.target.value })} data-testid={`salary-override-${k}`} />
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-[#0A0807] text-white p-4 grid grid-cols-3 gap-2 text-sm" data-testid="salary-totals">
              <div><div className="text-neutral-400 text-xs">Gross</div><b>{inr(data.breakup.gross)}</b></div>
              <div><div className="text-neutral-400 text-xs">Deductions</div><b>{inr(data.breakup.total_deductions)}</b></div>
              <div><div className="text-neutral-400 text-xs">Net pay</div><b className="text-[#E08A1E]" data-testid="salary-net-pay">{inr(data.breakup.net_pay)}</b></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => save({})} data-testid="salary-reset-button">Use formula for all</Button>
              <Button className={BTN} onClick={() => save(ov)} data-testid="salary-save-button">Save breakup</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
